import {
  Megaphone,
  Search,
  MonitorSmartphone,
  GraduationCap,
  BookOpen,
  Clapperboard,
  PenLine,
  Palette,
} from 'lucide-react';
import { img } from './site';

export const services = [
  {
    num: '01',
    slug: 'social-media-management-ads',
    icon: Megaphone,
    title: 'Social Media Management & Ads',
    shortTitle: 'Social Media & Ads',
    headline: 'Turn Your Social Media Into a Business Asset.',
    short:
      'Consistent, compelling social presence — strategy, content, community and paid campaigns that support real business growth.',
    description:
      'We help businesses build a consistent, compelling social media presence that connects with their audience, communicates their value, and supports business growth. From content strategy and account management to creative campaigns and paid social advertising, we help brands show up with purpose.',
    offerings: [
      'Social media strategy',
      'Content planning & calendars',
      'Social media management',
      'Creative content development',
      'Community engagement',
      'Meta advertising campaigns',
      'Audience targeting',
      'Campaign optimization',
      'Performance reporting',
    ],
    cta: 'Grow Your Social Presence',
    accent: '#064B2B',
    image: img('photo-1611926653458-09294b3142bf'),
    imageAlt: 'Smartphone showing social media content on a creative workspace desk',
  },
  {
    num: '02',
    slug: 'google-ads-seo',
    icon: Search,
    title: 'Google Ads & SEO',
    shortTitle: 'Google Ads & SEO',
    headline: 'Be Found When Your Customers Are Searching.',
    short:
      'Capture high-intent searches and build long-term organic visibility with strategies built to generate meaningful opportunities.',
    description:
      'We help businesses improve their search visibility and reach relevant audiences through Google Ads and search engine optimization. From capturing high-intent searches to building long-term organic visibility, we develop strategies that help your business compete for attention and generate meaningful opportunities.',
    offerings: [
      'Google Search Ads',
      'Display & YouTube advertising',
      'Keyword research',
      'Campaign setup & optimization',
      'Search engine optimization',
      'Technical SEO',
      'On-page SEO',
      'Off-page SEO & link-building strategy',
      'Local SEO',
      'Programmatic SEO',
      'Answer Engine Optimization (AEO)',
      'Conversion tracking & reporting',
    ],
    cta: 'Improve Your Visibility',
    accent: '#064B2B',
    image: img('photo-1460925895917-afdab827c52f'),
    imageAlt: 'Laptop displaying search analytics and marketing performance charts',
  },
  {
    num: '03',
    slug: 'website-development',
    icon: MonitorSmartphone,
    title: 'Website Development',
    shortTitle: 'Web Development',
    headline: 'Your Business Deserves a Website That Works.',
    short:
      'Beautiful, responsive, conversion-focused websites — from business sites and landing pages to e-commerce and digital platforms.',
    description:
      'We design and develop beautiful, responsive, user-friendly websites that communicate your brand, showcase your offerings, and create a seamless experience for your audience. From business websites to landing pages and digital platforms, we help businesses establish a stronger online presence.',
    offerings: [
      'Business websites',
      'Responsive website design',
      'Landing pages',
      'Portfolio websites',
      'E-commerce website development',
      'Website redesign',
      'Website maintenance',
      'Conversion-focused layouts',
      'Website performance optimization',
      'SEO-friendly website structure',
    ],
    cta: 'Build Your Website',
    accent: '#064B2B',
    image: img('photo-1467232004584-a241de8bcf5d'),
    imageAlt: 'Designer sketching a responsive website layout beside a laptop',
  },
  {
    num: '04',
    slug: 'digital-trainings',
    icon: GraduationCap,
    title: 'Digital Trainings',
    shortTitle: 'Digital Trainings',
    headline: 'Learn Skills. Unlock Opportunities.',
    short:
      'Practical digital training for individuals, entrepreneurs, professionals and teams — built around real-world application.',
    description:
      'We provide practical digital training designed to help individuals, entrepreneurs, professionals, and businesses develop valuable digital skills. Our approach combines practical learning, real-world applications, and tools that help learners apply their knowledge.',
    offerings: [
      'Digital marketing training',
      'Google Ads training',
      'SEO training',
      'Social media marketing',
      'AI tools & prompt engineering',
      'Digital business skills',
      'Practical workshops',
      'Professional development',
    ],
    cta: 'Explore Our Trainings',
    accent: '#064B2B',
    image: img('photo-1573164713619-24c711fe7878'),
    imageAlt: 'Young African women learning digital skills on laptops in a virtual class',
  },
  {
    num: '05',
    slug: 'digital-marketing-courses-ebooks',
    icon: BookOpen,
    title: 'Digital Marketing Courses & Ebooks',
    shortTitle: 'Courses & Ebooks',
    headline: 'Invest in Skills That Create Opportunities.',
    short:
      'Structured digital marketing courses and practical learning resources — learn at your own pace and apply skills to real business challenges.',
    description:
      'Access practical digital marketing courses and learning resources designed to help you understand digital marketing, develop valuable skills, and apply them to real business challenges. Learn at your own pace or explore structured training opportunities.',
    offerings: [
      'Complete Digital Marketing Course — ₦150,000',
      'Google Ads & SEO Mastery — ₦100,000',
      'Individual digital marketing courses — ₦50,000 each',
      'Ads Targeting & Conversion',
      'AI Prompt Engineering',
      'Practical advertising training',
      'Learning resources & ebooks',
    ],
    cta: 'Explore Our Courses',
    accent: '#064B2B',
    image: img('photo-1576267423445-b2e0074d68a4'),
    imageAlt: 'Young Africans learning tech together around a laptop in an online class',
    linkOverride: '/courses',
  },
  {
    num: '06',
    slug: 'content-production',
    icon: Clapperboard,
    title: 'Content Production & Creation',
    shortTitle: 'Content Production',
    headline: 'Make Your Brand Impossible to Ignore.',
    short:
      'Compelling visual and written content — from creative concepts to professionally produced stories that help brands stand out.',
    description:
      'We create compelling visual and written content that helps brands communicate their message, connect with audiences, and stand out in a crowded digital environment. From creative concepts to professionally produced content, we help businesses tell stories that matter.',
    offerings: [
      'Social media content',
      'Video production',
      'Promotional videos',
      'Brand storytelling',
      'Creative advertising concepts',
      'Scriptwriting',
      'Documentary production',
      'Voiceovers',
      'Copywriting',
      'Content strategy',
    ],
    cta: 'Create With Us',
    accent: '#064B2B',
    image: img('photo-1574717024653-61fd2cf4d44d'),
    imageAlt: 'Professional camera and lighting setup in a content production studio',
  },
  {
    num: '07',
    slug: 'research-writing',
    icon: PenLine,
    title: 'Research Writing Services',
    shortTitle: 'Research Writing',
    headline: 'Turn Complex Ideas Into Clear, Compelling Work.',
    short:
      'Structured research and professional writing support — clear, well-organized, purposeful documents for people and organizations.',
    description:
      'We provide structured research and professional writing support for individuals, businesses, and organizations that need clear, well-organized, and purposeful written content. Our services focus on research, organization, clarity, and communicating ideas effectively.',
    offerings: [
      'Research writing',
      'Article writing',
      'Business research',
      'Reports & professional documents',
      'Research-based content',
      'Editing & proofreading',
      'Structured writing support',
    ],
    cta: 'Discuss Your Project',
    accent: '#064B2B',
    image: img('photo-1455390582262-044cdead277a'),
    imageAlt: 'Writer editing a manuscript with a fountain pen at a refined desk',
  },
  {
    num: '08',
    slug: 'graphics-design-branding',
    icon: Palette,
    title: 'Graphics Design & Branding',
    shortTitle: 'Design & Branding',
    headline: 'Make Your Brand Look as Valuable as It Is.',
    short:
      'Distinctive visual identities and creative design — from brand identity systems to marketing materials that present you professionally.',
    description:
      'We help businesses build distinctive visual identities and communicate their value through creative graphic design and branding solutions. From brand identity to marketing materials, we create visuals that help businesses present themselves professionally and consistently.',
    offerings: [
      'Logo & brand identity design',
      'Brand guidelines',
      'Social media graphics',
      'Advertising creatives',
      'Marketing flyers',
      'Presentation design',
      'Business promotional materials',
      'Visual communication',
    ],
    cta: 'Build Your Brand',
    accent: '#064B2B',
    image: img('photo-1561070791-2526d30994b5'),
    imageAlt: 'Brand identity flat-lay with typography, color swatches and stationery',
  },
];

export const getService = (slug) => services.find((s) => s.slug === slug);
